import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, KanbanSquare, ListTree, Users, Layers, Building2,
  Bell, Search, Sun, Moon, Command, Menu, LogOut, Settings, Ticket, Activity, ChevronRight, FileBarChart,
  ShieldCheck, Mail, Network, CalendarDays, ArrowLeftRight , Sparkles, MessageCircle, IdCard, Boxes, X, UserRound} from "lucide-react";
import { useAuth, isAdmin, isTechnician } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import api from "@/lib/api";
import { initialsOf, hoursAgo } from "@/lib/mock";
import CommandPalette from "@/components/CommandPalette";
import ProfileDialog from "@/components/ProfileDialog";

const NAV = {
  portal: [
    { to: "/portal",           label: "Overview",     icon: LayoutDashboard, end: true },
    { to: "/portal/tickets",   label: "My Tickets",   icon: Ticket },
    { to: "/portal/my-assets", label: "My Assets",    icon: Boxes },
    { to: "/portal/help",      label: "Ask for Help", icon: MessageCircle },
    { to: "/portal/activity",  label: "Activity",     icon: Activity },
  ],
  admin: [
    { to: "/admin",             label: "Overview",       icon: LayoutDashboard, end: true },
    { to: "/admin/kanban",      label: "Kanban Board",   icon: KanbanSquare },
    { to: "/admin/queue",       label: "Ticket Queue",   icon: ListTree },
    { to: "/admin/my-requests", label: "My Requests",    icon: Ticket,   nonAdminOnly: true },
    { to: "/admin/help",        label: "Ask for Help",   icon: MessageCircle },
    { to: "/admin/reports",     label: "Reports",        icon: FileBarChart },
    { to: "/cam",               label: "Assets",         icon: Boxes },
    { to: "/admin/team",        label: "Users & Team",   icon: Users,    adminOnly: true },
    { to: "/admin/users/bulk-import", label: "Bulk Import",  icon: Users,    adminOnly: true },
    { to: "/admin/categories",  label: "Categories",     icon: Layers,   managerOnly: true },
    { to: "/admin/knowledge",   label: "AI Knowledge",   icon: Sparkles, adminOnly: true },
    { to: "/admin/settings",    label: "Settings",       icon: Settings, adminOnly: true },
  ],
  superadmin: [
    { to: "/superadmin",             label: "Platform",     icon: LayoutDashboard, end: true },
    { to: "/superadmin/institutes",  label: "Institutes",   icon: Building2 },
    { to: "/superadmin/admins",      label: "Admins",       icon: ShieldCheck },
    { to: "/superadmin/authorities", label: "Authorities",  icon: Network },
    { to: "/superadmin/users",       label: "All users",    icon: Users },
    { to: "/superadmin/tickets",     label: "All tickets",  icon: Ticket },
    { to: "/superadmin/email",       label: "Platform email", icon: Mail },
  ],
};

const ROLE_TITLE = {
  portal: "Staff Portal",
  admin:  "Admin Console",
  superadmin: "Platform Control",
};


// Cross-app hand-off. We ask our backend for a 60-second single-use token and
// send it in the URL FRAGMENT so it never reaches a server log or Referer header.
function PlatformSwitcher({ user }) {
  const [busy, setBusy] = useState(false);
  const eligible = !!(user?.institute?.sso_enabled);
  if (!eligible) return null;

  const go = async () => {
    setBusy(true);
    try {
      const r = await api.ssoIssue("events");
      if (!r?.token || !r?.redirect) throw new Error("Could not start sign-in hand-off.");
      window.location.href = `${r.redirect}#t=${encodeURIComponent(r.token)}`;
    } catch (e) {
      toast.error(e.message || "Could not open Events.");
      setBusy(false);
    }
  };

  return (
    <button
      onClick={go}
      disabled={busy}
      data-testid="app-switch-events"
      aria-label="Switch to Auditorium & Events"
      title="Switch to Auditorium & Events"
      className="group flex items-center gap-1.5 sm:gap-2 h-10 shrink-0 rounded-full bg-cobalt-500 hover:bg-cobalt-600 text-white pl-2.5 pr-3 sm:pl-3 sm:pr-3.5 shadow-[0_4px_16px_rgba(0,71,255,0.35)] transition-colors disabled:opacity-60 tap-shrink"
    >
      {busy ? (
        <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin shrink-0" />
      ) : (
        <CalendarDays className="h-4 w-4 shrink-0" />
      )}
      <span className="hidden sm:inline text-xs font-semibold whitespace-nowrap">
        {busy ? "Opening…" : "Events"}
      </span>
      {!busy && <ArrowLeftRight className="hidden sm:inline h-3 w-3 opacity-70 group-hover:translate-x-0.5 transition-transform" />}
    </button>
  );
}

export default function Shell({ role, title, subtitle, actions, children }) {
  const { theme, toggleTheme, user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [recent, setRecent] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const canManage = isAdmin(user) || user?.role === "authority";
  const nav = (NAV[role] || []).filter((item) =>
    (!item.adminOnly || isAdmin(user)) &&
    (!item.managerOnly || canManage) &&
    // central admins already see everything in the queue, so a separate
    // "My Requests" tab only helps heads and technicians
    (!item.nonAdminOnly || !isAdmin(user)));

  // Native apps only get ~5 slots in a bottom tab bar. Show the first 4
  // and fold anything else (plus settings/sign-out) behind a "More" tab
  // that opens the full drawer — same nav items, no functionality lost.
  const tabItems = nav.slice(0, 4);
  const hasMore = nav.length > 4;

  // Pull a lightweight recent-tickets feed for the bell popover
  useEffect(() => {
    let cancelled = false;
    api.tickets({ limit: 6 })
      .then((res) => { if (!cancelled) setRecent(res?.tickets || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [location.pathname]);

  const isActive = (item) =>
    item.end ? location.pathname === item.to : location.pathname.startsWith(item.to);

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const instituteLabel = user?.institute?.name ? `${user.institute.name}${user.institute.code ? ` · ${user.institute.code}` : ""}` : "Solution";

  return (
    <div className="min-h-screen flex text-[15px]">
      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 left-0 z-50 lg:z-40 h-screen w-[85vw] max-w-[300px] lg:w-[264px] shrink-0 p-3 lg:p-4 safe-top safe-left transition-transform duration-300 ease-out ${
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="sidebar"
      >
        <div className="glass rounded-3xl h-full flex flex-col p-4 overflow-y-auto touch-scroll safe-bottom">
          <div className="flex items-center justify-between px-2 py-1.5">
          <Link to="/" className="flex items-center gap-2.5">
            {user?.institute?.logo_url ? (
              <img
                src={user.institute.logo_url}
                alt={user.institute.name || "Institute logo"}
                className="h-8 w-8 rounded-xl object-cover shrink-0"
                onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "grid"; }}
              />
            ) : null}
            <div
              className="h-8 w-8 rounded-xl bg-white border border-black/5 grid place-items-center shrink-0 shadow-sm"
              style={user?.institute?.logo_url ? { display: "none" } : undefined}
            >
              <img src="/brand/solution-mark-t.png" alt="Solution" className="h-6 w-6 object-contain" />
            </div>
            <div className="font-display text-lg font-extrabold tracking-tighter">
              Solu<span className="text-cobalt-500">tion</span>
            </div>
          </Link>
            <button
              onClick={() => setMobileOpen(false)}
              className="lg:hidden h-9 w-9 grid place-items-center rounded-xl text-muted-foreground bg-black/[0.03] dark:bg-white/5 hover:bg-black/5 tap-shrink"
              aria-label="Close menu"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="mt-4 rounded-2xl bg-white/40 dark:bg-white/5 border border-white/60 dark:border-white/5 p-3" data-testid="sidebar-institute">
            <div className="overline">Institute</div>
            <div className="mt-1 font-display text-[15px] font-semibold tracking-tight truncate">
              {user?.institute?.name || "—"}
            </div>
            <div className="text-[11px] text-mono uppercase tracking-widest text-muted-foreground mt-0.5">
              {user?.institute?.code || ""}
            </div>
          </div>

          {/* Anyone who both works a queue AND raises their own tickets can flip
              between the two views without hunting for a URL. */}
          {(canManage || isTechnician(user)) && (
            <div className="mt-4 flex gap-1 p-1 rounded-full bg-black/5 dark:bg-white/5" data-testid="view-switch">
              <button
                onClick={() => navigate("/admin")}
                className={`flex-1 text-[11px] font-medium py-1.5 rounded-full transition-colors ${role !== "portal" ? "bg-white dark:bg-zinc-800 shadow-sm" : "opacity-60 hover:opacity-100"}`}
              >
                {isAdmin(user) ? "Admin" : "My work"}
              </button>
              <button
                onClick={() => navigate("/portal")}
                className={`flex-1 text-[11px] font-medium py-1.5 rounded-full transition-colors ${role === "portal" ? "bg-white dark:bg-zinc-800 shadow-sm" : "opacity-60 hover:opacity-100"}`}
              >
                My requests
              </button>
            </div>
          )}

          <nav className="mt-4 flex-1 space-y-1" data-testid="sidebar-nav">
            <div className="overline px-3 mb-2">{ROLE_TITLE[role]}</div>
            {nav.map((item) => {
              const Icon = item.icon;
              const active = isActive(item);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g,'-')}`}
                  className={`group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                    active
                      ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 shadow-[0_6px_24px_rgba(0,71,255,0.25)]"
                      : "hover:bg-white/60 dark:hover:bg-white/5 text-foreground/80"
                  }`}
                >
                  <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                  <span className="flex-1">{item.label}</span>
                  {active && <ChevronRight className="h-3.5 w-3.5" />}
                </Link>
              );
            })}
          </nav>

          <div className="mt-4 rounded-2xl bg-white/60 dark:bg-white/5 border border-white/70 dark:border-white/5 p-3">
            <div className="flex items-center gap-2.5">
              <Avatar className="h-9 w-9">
                <AvatarFallback className="bg-cobalt-500 text-white font-display font-bold">
                  {initialsOf(user?.name || user?.email || "?")}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name || user?.email}</div>
                <div className="text-[11px] text-muted-foreground truncate capitalize">{user?.role}</div>
              </div>
            </div>
            <div className="mt-2 flex gap-1.5">
              <Button
                data-testid="sidebar-edit-profile"
                size="sm"
                variant="ghost"
                onClick={() => { setMobileOpen(false); setProfileOpen(true); }}
                className="h-8 flex-1 justify-start gap-1.5 text-xs"
              >
                <UserRound className="h-3.5 w-3.5" /> Edit Profile
              </Button>
              <Button
                data-testid="sidebar-signout"
                size="sm"
                variant="ghost"
                onClick={doLogout}
                className="h-8 flex-1 justify-start gap-1.5 text-xs"
              >
                <LogOut className="h-3.5 w-3.5" /> Sign out
              </Button>
            </div>
          </div>

          <a href="https://arventiqlabs.com" target="_blank" rel="noreferrer"
             className="mt-3 flex flex-col items-center gap-1.5 opacity-80 hover:opacity-100 transition-opacity">
            <span className="text-[9px] text-mono uppercase tracking-widest text-muted-foreground">Powered by</span>
            <img src="/brand/arventiq-full.png" alt="Arventiq Labs" className="h-5 w-auto dark:brightness-0 dark:invert" />
          </a>
        </div>
      </aside>

      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          aria-label="Close menu"
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px] lg:hidden animate-in fade-in duration-200"
        />
      )}

      <main className="flex-1 min-w-0">
        <div className="sticky top-0 z-20 px-3 sm:px-6 lg:px-8 pt-3 lg:pt-4 safe-top" data-testid="topbar">
          <div className="glass-strong rounded-2xl px-3 sm:px-4 py-2.5 sm:py-3 flex items-center gap-1.5 sm:gap-3">
            <button
              className="lg:hidden inline-flex items-center justify-center h-10 w-10 shrink-0 rounded-lg bg-white/60 dark:bg-white/5 tap-shrink"
              onClick={() => setMobileOpen(true)}
              data-testid="topbar-menu"
            >
              <Menu className="h-4 w-4" />
            </button>
            <div className="min-w-0 flex-1">
              <div className="overline">{instituteLabel}</div>
              <h1 className="font-display text-xl sm:text-2xl font-extrabold tracking-tighter truncate" data-testid="page-title">{title}</h1>
              {subtitle && <div className="text-xs text-muted-foreground truncate">{subtitle}</div>}
            </div>

            <div className="flex items-center gap-1.5 sm:gap-2 shrink-0" data-testid="topbar-controls">
            <PlatformSwitcher user={user} />

            <button
              data-testid="topbar-search"
              onClick={() => setCmdOpen(true)}
              className="hidden md:flex items-center gap-2 rounded-full bg-white/70 dark:bg-white/5 border border-white/70 dark:border-white/10 h-10 px-3.5 min-w-[240px] text-sm text-muted-foreground hover:text-foreground"
            >
              <Search className="h-4 w-4" />
              <span className="flex-1 text-left">Search tickets, techs, campuses…</span>
              <kbd className="ml-2 hidden sm:inline-flex items-center gap-0.5 rounded-md bg-black/5 dark:bg-white/10 px-1.5 py-0.5 text-[10px] font-mono">
                <Command className="h-3 w-3" />K
              </kbd>
            </button>
            <button
              aria-label="Search"
              onClick={() => setCmdOpen(true)}
              className="md:hidden h-10 w-10 shrink-0 rounded-full bg-white/70 dark:bg-white/5 border border-white/70 dark:border-white/10 grid place-items-center hover:text-cobalt-500 tap-shrink"
            >
              <Search className="h-4 w-4" />
            </button>

            <Popover>
              <PopoverTrigger asChild>
                <button
                  data-testid="topbar-notifications"
                  className="relative h-10 w-10 shrink-0 rounded-full bg-white/70 dark:bg-white/5 border border-white/70 dark:border-white/10 grid place-items-center hover:text-cobalt-500 tap-shrink"
                >
                  <Bell className="h-4 w-4" />
                  {recent.length > 0 && <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-cobalt-500 ring-2 ring-white dark:ring-zinc-900" />}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-[calc(100vw-1.5rem)] sm:w-[360px] p-0 rounded-2xl border-white/70 dark:border-white/10 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-xl">
                <div className="p-4 border-b">
                  <div className="overline">Recent tickets</div>
                  <div className="font-display text-lg font-extrabold tracking-tighter">Live activity</div>
                </div>
                <div className="max-h-[340px] overflow-y-auto touch-scroll divide-y">
                  {recent.length === 0 && <div className="p-6 text-xs text-muted-foreground">No recent tickets.</div>}
                  {recent.map((a) => (
                    <div key={a.id} className="p-3 flex gap-3 items-start hover:bg-black/[0.02] dark:hover:bg-white/[0.02]">
                      <span className="mt-1 h-2 w-2 rounded-full bg-cobalt-500" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate"><b>{a.ticket_no}</b> · {a.title}</div>
                        <div className="text-xs text-muted-foreground capitalize">{a.status?.replace("_"," ")} · {a.priority}</div>
                      </div>
                      <div className="text-[10px] text-mono uppercase text-muted-foreground">{hoursAgo(a.updated_at || a.created_at)}</div>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            <button
              data-testid="topbar-theme-toggle"
              onClick={toggleTheme}
              className="hidden sm:grid h-10 w-10 shrink-0 rounded-full bg-white/70 dark:bg-white/5 border border-white/70 dark:border-white/10 place-items-center hover:text-cobalt-500 tap-shrink"
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>

            {actions}
            </div>
          </div>
        </div>

        <div className="px-4 sm:px-6 lg:px-8 py-6 space-y-6 pb-28 lg:pb-8" data-testid="page-content">
          {children}
        </div>

        <div className="hidden lg:flex px-4 sm:px-6 lg:px-8 pb-8 pt-2 text-xs text-muted-foreground justify-between">
          <span>Solution v4.2</span>
        </div>
      </main>

      {/* Bottom tab bar — native-app style primary navigation for phones */}
      <nav
        className={`lg:hidden fixed bottom-0 left-0 right-0 z-30 safe-bottom pointer-events-none transition-opacity duration-200 ${mobileOpen ? "opacity-0 pointer-events-none" : "opacity-100"}`}
        data-testid="bottom-tabbar"
      >
        <div className="mx-3 mb-3 pointer-events-auto glass-strong rounded-[26px] px-2 py-2 flex items-stretch gap-0.5 border border-white/60 dark:border-white/10 shadow-[0_12px_44px_rgba(15,23,42,0.20)]">
          {tabItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item);
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`tab-${item.label.toLowerCase().replace(/\s/g,'-')}`}
                className="relative flex-1 min-w-0 flex flex-col items-center justify-center gap-1 rounded-2xl py-1.5 tap-shrink"
              >
                {active && <span className="absolute top-0 left-1/2 -translate-x-1/2 h-1 w-7 rounded-full bg-cobalt-500" />}
                <span className={`grid place-items-center h-9 w-9 rounded-2xl transition-all duration-200 ${active ? "bg-cobalt-500 text-white shadow-[0_6px_18px_rgba(6,84,252,0.45)] scale-105" : "text-foreground/55"}`}>
                  <Icon className="h-[18px] w-[18px]" strokeWidth={active ? 2.1 : 1.9} />
                </span>
                <span className={`text-[10px] leading-none truncate max-w-full px-0.5 transition-colors ${active ? "text-cobalt-500 font-semibold" : "text-foreground/50 font-medium"}`}>
                  {item.label}
                </span>
              </Link>
            );
          })}
          {hasMore && (
            <button
              onClick={() => setMobileOpen(true)}
              data-testid="tab-more"
              className="relative flex-1 min-w-0 flex flex-col items-center justify-center gap-1 rounded-2xl py-1.5 tap-shrink"
            >
              <span className="grid place-items-center h-9 w-9 rounded-2xl text-foreground/55 border border-dashed border-foreground/20">
                <Menu className="h-[18px] w-[18px]" strokeWidth={1.9} />
              </span>
              <span className="text-[10px] leading-none font-medium text-foreground/50">More</span>
            </button>
          )}
        </div>
      </nav>

      <CommandPalette open={cmdOpen} setOpen={setCmdOpen} role={role} />
      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />
    </div>
  );
}
